import CasinoRoundedIcon from '@mui/icons-material/CasinoRounded';
import SportsSoccerRoundedIcon from '@mui/icons-material/SportsSoccerRounded';
import { useFeatureIsOn } from '@growthbook/growthbook-react';
import { Box } from '@mui/material';
import { FEATURE_FLAG } from '../../../../constants/featureFlags';
import PromoIcon from '../../../icons/PromoIcon';
import { NEOBET_FEATURED_TAB_PATH } from '../constants';
import { BettingDrawerLoadingState, CasinoDrawerList, SportsbookDrawerList } from './BettingDrawerLists';
import NeobetCompactSidebarButton from './NeobetCompactSidebarButton';
import NeobetInstallAppButton from './NeobetInstallAppButton';
import SidebarLanguageSection from './SidebarLanguageSection';
import type { BettingDrawerData } from './useBettingDrawerData';

const styles = {
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: 1.25,
    alignItems: 'center',
    px: 0,
    pb: 1.5,
    pt: 0.5,
  },
} as const;

type CompactBettingDrawerContentProps = {
  drawerData: BettingDrawerData;
  isActive: boolean;
};

const CompactBettingDrawerContent = ({ drawerData, isActive }: CompactBettingDrawerContentProps) => {
  const {
    navigate,
    t,
    hasSportsbookAccess,
    hasCasinoAccess,
    isSectionExpanded,
    activateSection,
    isSportsLoading,
    isSportsFetching,
    isCategoriesLoading,
  } = drawerData;

  const promotionsEnabled = useFeatureIsOn(FEATURE_FLAG.PROMOTIONS_ENABLED);
  const isPromotionsSelected = drawerData.location.pathname.startsWith('/promotions');
  const casinoLabel = t('casino');

  const compactSections = [
    {
      key: 'casino',
      enabled: hasCasinoAccess,
      isActive: isSectionExpanded('casino'),
      isLoading: isCategoriesLoading,
      label: casinoLabel,
      testId: 'neobet-sidebar-section-casino',
      flyoutTestId: 'neobet-sidebar-section-casino-flyout',
      icon: <CasinoRoundedIcon fontSize='small' />,
      flyoutContent: <CasinoDrawerList drawerData={drawerData} />,
      onClick: () => {
        activateSection('casino');
        navigate('/casino');
      },
    },
    {
      key: 'sportsbook',
      enabled: hasSportsbookAccess,
      isActive: isSectionExpanded('sportsbook'),
      isLoading: isSportsLoading || isSportsFetching,
      label: t('sportsbook', 'Sportsbook'),
      testId: 'neobet-sidebar-section-sportsbook',
      flyoutTestId: 'neobet-sidebar-section-sportsbook-flyout',
      icon: <SportsSoccerRoundedIcon fontSize='small' />,
      flyoutContent: <SportsbookDrawerList drawerData={drawerData} />,
      onClick: () => {
        activateSection('sportsbook');
        navigate(NEOBET_FEATURED_TAB_PATH);
      },
    },
  ];

  return (
    <Box sx={styles.root}>
      {compactSections
        .filter((section) => section.enabled)
        .map((section) => {
          const flyoutContent = section.isLoading ? <BettingDrawerLoadingState /> : section.flyoutContent;

          return (
            <NeobetCompactSidebarButton
              key={section.key}
              isActive={section.isActive}
              onClick={section.onClick}
              aria-label={section.label}
              testId={section.testId}
              flyoutTestId={section.flyoutTestId}
              flyoutContent={isActive ? flyoutContent : undefined}
            >
              {section.icon}
            </NeobetCompactSidebarButton>
          );
        })}

      {promotionsEnabled && (
        <NeobetCompactSidebarButton
          onClick={() => navigate('/promotions')}
          isActive={isPromotionsSelected}
          aria-label={t('promotions', 'Promotions')}
          testId='neobet-sidebar-section-promotions'
        >
          <PromoIcon fontSize='small' sx={{ color: isPromotionsSelected ? 'primary.main' : 'text.primary' }} />
        </NeobetCompactSidebarButton>
      )}

      <NeobetInstallAppButton isCompact />

      <SidebarLanguageSection isCompact isActive={isActive} />
    </Box>
  );
};

export default CompactBettingDrawerContent;
